import { computed, watch, type Ref } from "vue";

import { useBrowserStore } from "@/composables/useBrowserStore";
import { browser } from "@/services/browser";
import { locale } from "@/utils/locale";
import { isRecord, property } from "@/utils/unknown";

/*
 * Tracks the user's layout direction preference and applies the resolved
 * direction to the document so CSS logical properties and native bidi can
 * mirror the UI.
 *
 * Preference mirrors theme: `ltr` / `rtl` / `system`. `system` follows the
 * OS/browser locale's text direction (e.g. `he`, `ar` → rtl).
 *
 * Ownership: the returned `release()` stops the persistence watcher,
 * document.dir application watcher, and cross-window storage listener —
 * callers (App.vue / provideSatelliteContext) own it.
 */

export type Direction = "ltr" | "rtl";
export type DirectionPreference = Direction | "system";

const STORAGE_KEY = "direction";

/** ISO 639-1 primary subtags that are written RTL. */
const RTL_PRIMARY_SUBTAGS = new Set([
  "ar",
  "fa",
  "he",
  "ur",
  "yi",
  "ps",
  "sd",
  "ug",
  "dv",
  "ha",
]);

const parseDirectionPreference = (value: string): DirectionPreference => {
  switch (value) {
    case "rtl":
    case "ltr":
    case "system":
      return value;
    default:
      return "system";
  }
};

/**
 * Resolve a BCP-47 tag to a layout direction. Prefers `Intl.Locale`
 * text-info when available, otherwise a primary-subtag fallback list.
 */
function directionFromTextInfo(value: unknown): Direction | undefined {
  if (!isRecord(value)) return undefined;
  const direction = property(value, "direction");
  return direction === "rtl" || direction === "ltr" ? direction : undefined;
}

export function directionFromLocale(tag: string): Direction {
  try {
    const loc: unknown = new Intl.Locale(tag);
    const getTextInfo = property(loc, "getTextInfo");
    if (typeof getTextInfo === "function") {
      const fromGetter = directionFromTextInfo(
        Reflect.apply(getTextInfo, loc, [])
      );
      if (fromGetter) return fromGetter;
    }
    const fromProp = directionFromTextInfo(property(loc, "textInfo"));
    if (fromProp) return fromProp;
  } catch {
    // Invalid locale tags fall through to the subtag list.
  }
  const primary = tag.toLowerCase().split("-")[0] ?? "";
  return RTL_PRIMARY_SUBTAGS.has(primary) ? "rtl" : "ltr";
}

export function systemDirection(): Direction {
  return directionFromLocale(locale.get());
}

export function applyDocumentDirection(direction: Direction) {
  document.documentElement.dir = direction;
}

export default function useDirection(): {
  directionPreference: Ref<DirectionPreference>;
  direction: Ref<Direction>;
  isRtl: Ref<boolean>;
  release: () => void;
} {
  const { syncRawState } = useBrowserStore();
  // Default to system so layout follows the OS/browser locale unless the
  // user overrides it (same shape as the theme preference).
  const stored = syncRawState(STORAGE_KEY, "system");
  const directionPreference = computed<DirectionPreference>({
    get: () => parseDirectionPreference(stored.value),
    set: (value) => {
      stored.value = value;
    },
  });
  const direction = computed<Direction>(() =>
    directionPreference.value === "system"
      ? systemDirection()
      : directionPreference.value
  );
  const isRtl = computed(() => direction.value === "rtl");

  applyDocumentDirection(direction.value);
  // Sync flush so toggling Preferences updates layout before paint.
  const stop = watch(
    direction,
    (value) => {
      applyDocumentDirection(value);
    },
    { flush: "sync" }
  );

  // Other windows (dashboard / menubar / meeting) share this origin's
  // localStorage; pick up Preference changes without a reload.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    const next = parseDirectionPreference(
      event.newValue ?? browser.store.getRaw(STORAGE_KEY, "system")
    );
    if (stored.value !== next) stored.value = next;
  };
  window.addEventListener("storage", onStorage);

  return {
    directionPreference,
    direction,
    isRtl,
    release: () => {
      stop();
      window.removeEventListener("storage", onStorage);
      stored.release();
    },
  };
}
